import { createFileRoute, Link } from "@tanstack/react-router";
import { getPdfToolBySlug, relatedPdfTools } from "@/lib/pdf-catalog";
import { getPdfToolContent } from "@/lib/pdf-content";
import { SiteFooter, SiteHeader } from "@/components/tools/ToolShell";
import { getEsPdfSlugForEnSlug } from "@/lib/es-pdf-catalog";
import { getDePdfSlugForEnSlug } from "@/lib/de-pdf-catalog";
import { getPtBrPdfSlugForEnSlug } from "@/lib/pt-br-pdf-catalog";
import { getFrPdfSlugForEnSlug } from "@/lib/fr-pdf-catalog";
import {
  ArrowLeft,
  BookOpen,
  Sparkles,
  CheckCircle2,
  HelpCircle,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/pdf-tools/$slug")({
  loader: ({ params }) => {
    const pdfTool = getPdfToolBySlug(params.slug);
    return { pdfTool: pdfTool || null, slug: params.slug };
  },
  head: ({ params }) => {
    const tool = getPdfToolBySlug(params.slug);
    if (!tool) return { meta: [{ title: "PDF Tool — Picamini.online" }] };

    const content = getPdfToolContent(tool.slug, tool.name);
    const canonical = `https://picamini.online/pdf-tools/${tool.slug}`;
    const esUrl = `https://picamini.online/es/pdf-tools/${getEsPdfSlugForEnSlug(tool.slug)}`;
    const deUrl = `https://picamini.online/de/pdf-tools/${getDePdfSlugForEnSlug(tool.slug)}`;
    const ptUrl = `https://picamini.online/pt-br/pdf-tools/${getPtBrPdfSlugForEnSlug(tool.slug)}`;
    const frUrl = `https://picamini.online/fr/pdf-tools/${getFrPdfSlugForEnSlug(tool.slug)}`;

    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `${tool.name} - PDF Toolkit Pro`,
      "url": canonical,
      "description": content.seoDescription,
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "All",
      "browserRequirements": "Requires JavaScript. Requires HTML5.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
      },
      "datePublished": "2024-01-15",
      "dateModified": "2026-09-15",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", "article p:first-of-type", ".tool-description"]
      },
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://picamini.online/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "PDF Toolkit Pro",
          "item": "https://picamini.online/pdf-tools",
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": tool.name,
          "item": canonical,
        },
      ],
    };

    const faqSchema = content.faq && content.faq.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    } : null;

    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify(webAppSchema),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema),
      },
    ];

    if (faqSchema) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(faqSchema),
      });
    }

    return {
      meta: [
        { title: content.seoTitle },
        { name: "description", content: content.seoDescription },
        {
          name: "keywords",
          content: tool.keywords?.join(", ") || `${tool.name.toLowerCase()}, online pdf tool, client side pdf, picamini`,
        },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${tool.name} - Free Online PDF Tool on Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: content.seoTitle },
        { name: "twitter:description", content: content.seoDescription },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "en", href: canonical },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: ptUrl },
        { rel: "alternate", hrefLang: "fr", href: frUrl },
        { rel: "alternate", hrefLang: "x-default", href: canonical },
      ],
      scripts,
    };
  },
  component: SinglePdfToolPage,
});

function SinglePdfToolPage() {
  const { pdfTool, slug } = Route.useLoaderData();
  const activeTool = pdfTool || getPdfToolBySlug(slug);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState("2800px");

  useEffect(() => {
    let lastHeight = 0;
    let observer: MutationObserver | null = null;

    const updateHeight = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        try {
          const doc = iframeRef.current.contentWindow.document;
          const container = doc.body;
          if (container) {
            const h = Math.ceil(container.scrollHeight || container.getBoundingClientRect().height);
            if (h > 200 && Math.abs(h - lastHeight) > 10) {
              lastHeight = h;
              setIframeHeight(`${h + 60}px`);
            }
          }
        } catch {
          // Fallback height used if frame cross-origin
        }
      }
    };

    const attachListeners = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        try {
          const doc = iframeRef.current.contentWindow.document;
          if (doc && doc.body) {
            updateHeight();

            if (!observer) {
              observer = new MutationObserver(updateHeight);
              observer.observe(doc.body, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
              });
            }

            doc.addEventListener("change", updateHeight);
            doc.addEventListener("input", updateHeight);
            doc.addEventListener("click", () => setTimeout(updateHeight, 150));
            doc.addEventListener("drop", () => setTimeout(updateHeight, 300));
          }
        } catch {
          // Fallback
        }
      }
    };

    const t1 = setTimeout(attachListeners, 200);
    const t2 = setTimeout(attachListeners, 600);
    const t3 = setTimeout(attachListeners, 1200);
    const interval = setInterval(updateHeight, 1500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearInterval(interval);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [slug]);

  if (!activeTool) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <main className="flex-1 py-20 text-center space-y-4">
          <h2 className="text-xl font-bold text-foreground">PDF Tool Not Found</h2>
          <Link to="/pdf-tools" className="inline-block text-sm font-bold text-primary hover:underline">
            ← Return to PDF Tools Hub
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const content = getPdfToolContent(activeTool.slug, activeTool.name);
  const related = relatedPdfTools(activeTool.slug);

  const toolHtmlMap: Record<string, string> = {
    "merge-pdf": "merge.html",
    "split-pdf": "split.html",
    "organize-pdf": "organize.html",
    "rotate-pdf": "rotate.html",
    "extract-pages": "extract-pages.html",
    "remove-pages": "remove-pages.html",
    "edit-pdf": "edit-pdf.html",
    "page-numbers": "page-numbers.html",
    "watermark-pdf": "watermark.html",
    "compress-pdf": "compress.html",
    "grayscale-pdf": "grayscale.html",
    "repair-pdf": "repair.html",
    "image-to-pdf": "jpg-to-pdf.html",
    "word-to-pdf": "word-to-pdf.html",
    "excel-to-pdf": "excel-to-pdf.html",
    "ppt-to-pdf": "ppt-to-pdf.html",
    "html-to-pdf": "html-to-pdf.html",
    "txt-to-pdf": "txt-to-pdf.html",
    "pdf-to-image": "pdf-to-jpg.html",
    "pdf-to-word": "pdf-to-word.html",
    "pdf-to-excel": "pdf-to-excel.html",
    "pdf-to-text": "pdf-to-text.html",
    "protect-pdf": "protect-pdf.html",
    "unlock-pdf": "unlock-pdf.html",
    "sign-pdf": "sign-pdf.html",
    "ocr-pdf": "ocr.html",
  };

  const htmlFileName = toolHtmlMap[activeTool.slug] || `${activeTool.slug}.html`;
  const iframeSrc = `/pdf-tools/tools/${htmlFileName}?embed=true`;

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader />

      <main className="flex-1 bg-[#f8fafc]">
        {/* SEO Header Section */}
        <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 pt-6 pb-4">
          <nav className="no-print text-sm">
            <Link
              to="/pdf-tools"
              className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground font-semibold"
            >
              <ArrowLeft className="size-4" /> PDF Toolkit Pro Hub
            </Link>
          </nav>

          <div className="no-print pt-3 pb-2">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-primary">
              PDF Toolkit Pro — {activeTool.category}
            </p>
            <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl tracking-tight text-foreground">
              {content.h1Title || `${activeTool.name} Online Free — 100% Client-Side`}
            </h1>
            <p className="mt-2.5 max-w-4xl text-base text-muted-foreground leading-relaxed">
              {content.intro}
            </p>
          </div>
        </div>

        {/* Interactive PDF Tool Workspace & Original SEO Article (Iframe) */}
        <div className="w-full mx-auto">
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title={activeTool.name}
            className="w-full border-0 transition-all duration-300 bg-transparent"
            style={{ height: iframeHeight, overflow: "hidden" }}
            allow="clipboard-read; clipboard-write"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>

        {/* Rich SEO Content Below Tool */}
        <div className="no-print mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-12 space-y-10">
          {/* 1. What is section */}
          {content.whatIs && content.whatIs.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="size-5 text-primary" /> What is {activeTool.name}?
              </h2>
              <div className="space-y-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
                {content.whatIs.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            </section>
          )}

          {/* 2. How to Use & Features */}
          <div className="grid gap-6 md:grid-cols-2">
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Sparkles className="size-5 text-primary" /> How to Use {activeTool.name}
              </h2>
              <ol className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.howTo.map((step, i) => (
                  <li key={step} className="flex gap-3">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/10 text-primary font-bold text-xs">
                      {i + 1}
                    </span>
                    <span className="leading-snug">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <CheckCircle2 className="size-5 text-primary" /> Key Features
              </h2>
              <ul className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.features.map((f) => (
                  <li key={f} className="flex gap-2.5 items-start">
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                    <span className="leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* 3. Security Guarantee Box */}
          <section className="surface-card p-6 sm:p-8 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 space-y-3">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <ShieldCheck className="size-6 text-emerald-600 dark:text-emerald-400" />
              100% Client-Side Privacy Guarantee
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your files and document data processed with <strong>{activeTool.name}</strong> are never uploaded to any cloud server. Everything is calculated in local device memory for maximum security and privacy.
            </p>
          </section>

          {/* 4. FAQ Section */}
          {content.faq && content.faq.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <HelpCircle className="size-5 text-primary" /> Frequently Asked Questions (FAQ)
              </h2>
              <dl className="space-y-4 divide-y divide-border/60">
                {content.faq.map((item, idx) => (
                  <div key={item.q} className={idx > 0 ? "pt-4" : ""}>
                    <dt className="font-bold text-sm sm:text-base text-foreground">{item.q}</dt>
                    <dd className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* 5. Related PDF Tools Grid */}
          <section className="pt-4">
            <h2 className="text-xl font-bold text-foreground mb-4">Related PDF Tools</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/pdf-tools/$slug"
                  params={{ slug: r.slug }}
                  className="surface-card p-4 rounded-xl border bg-card transition-all hover:-translate-y-0.5 hover:shadow-md space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="size-4 text-primary" />
                    <p className="font-bold text-sm text-foreground">{r.name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{r.tagline}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
